/**
 * Mutation Observer Manager
 * Manages the observation of DOM changes to process dynamically added links
 */

/**
 * Create and configure a MutationObserver to watch for new links
 * 
 * @param {Function} processLinks Function to process links when new ones are added
 * @param {Object} options Configuration options
 * @param {number} options.throttleTime Time in ms to throttle processing (default: 250)
 * @return {MutationObserver} The configured MutationObserver
 */
export function createLinkObserver(processLinks, options = {}) {
    // Default throttle time in milliseconds
    const throttleTime = options.throttleTime || 250;
    
    // Process state
    const state = {
        processPending: false,
        lastProcessTime: 0,
        throttleTimeout: null
    };
    
    // Throttled version of processLinks using requestAnimationFrame
    const throttledProcessLinks = () => {
        if (!state.processPending) {
            return;
        }
        
        const now = Date.now();
        
        if (now - state.lastProcessTime >= throttleTime) {
            // Time has elapsed, process links now
            processLinks();
            state.lastProcessTime = now;
            state.processPending = false;
        } else {
            // Clear any existing timeout
            if (state.throttleTimeout) {
                clearTimeout(state.throttleTimeout);
            }
            
            // Schedule processing using requestAnimationFrame for better performance
            state.throttleTimeout = setTimeout(() => {
                requestAnimationFrame(() => {
                    processLinks();
                    state.lastProcessTime = Date.now();
                    state.processPending = false;
                });
            }, throttleTime - (now - state.lastProcessTime));
        }
    };
    
    // Create MutationObserver to watch for new links
    const observer = new MutationObserver((mutations) => {
        let newLinks = false;
        
        mutations.forEach((mutation) => {
            // Look for added nodes
            if (mutation.addedNodes && mutation.addedNodes.length > 0) {
                for (let i = 0; i < mutation.addedNodes.length; i++) {
                    const node = mutation.addedNodes[i];
                    
                    // Check if the node is an element
                    if (node.nodeType === 1) {
                        // If the node itself is a link
                        if (node.tagName === 'A') {
                            newLinks = true;
                            break;
                        }
                        
                        // Check for links within the added node
                        const nodeLinks = node.getElementsByTagName('a');
                        if (nodeLinks && nodeLinks.length > 0) {
                            newLinks = true;
                            break;
                        }
                    }
                }
            }
        });
        
        // If new links were added, schedule processing
        if (newLinks) {
            state.processPending = true;
            throttledProcessLinks();
        }
    });
    
    return observer;
}

/**
 * Start observing the document for mutations
 * 
 * @param {MutationObserver} observer The MutationObserver to use
 * @param {Element} target The target element to observe (default: document.body)
 * @param {Object} config MutationObserver configuration
 */
export function startObserving(observer, target = document.body, config = { childList: true, subtree: true }) {
    observer.observe(target, config);
}

/**
 * Stop observing the document for mutations
 * 
 * @param {MutationObserver} observer The MutationObserver to stop
 */
export function stopObserving(observer) {
    if (observer) {
        observer.disconnect();
    }
}